<div class="w-full edit-court-dialog-content">
    <div class="p-4" style="min-height: fit-content;">
        <!-- Header -->
        <div class="flex justify-between items-center mb-4">
            <h2 class="text-2xl font-bold">Edit Court</h2>
            <button mat-icon-button (click)="onCancel()" class="text-gray-500 hover:text-gray-700">
                <mat-icon>close</mat-icon>
            </button>
        </div>

        <!-- Venue and Company Info -->
        <div class="mb-6 bg-blue-50 rounded-lg p-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <div class="flex items-center">
                        <mat-icon class="mr-2 text-blue-600">location_on</mat-icon>
                        <div>
                            <div class="text-sm font-medium text-gray-600">Venue</div>
                            <div class="font-semibold text-gray-900">{{venueName}}</div>
                        </div>
                    </div>
                </div>
                <div>
                    <div class="flex items-center">
                        <mat-icon class="mr-2 text-blue-600">business</mat-icon>
                        <div>
                            <div class="text-sm font-medium text-gray-600">Company</div>
                            <div class="font-semibold text-gray-900">{{companyName}}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <form [formGroup]="courtForm" (ngSubmit)="onSubmit()">
            <!-- Basic Information -->
            <div class="mb-4">
                <h3 class="text-lg font-semibold mb-3 flex items-center">
                    <mat-icon class="mr-2">info</mat-icon>
                    Basic Information
                </h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <!-- Name -->
                    <mat-form-field class="w-full">
                        <mat-label>Court Name</mat-label>
                        <input matInput formControlName="name" required>
                        <mat-error *ngIf="courtForm.get('name')?.hasError('required')">
                            Court name is required
                        </mat-error>
                    </mat-form-field>

                    <!-- Sport -->
                    <mat-form-field class="w-full">
                        <mat-label>Sport</mat-label>
                        <mat-select formControlName="sports" required>
                            <mat-option *ngFor="let sport of allSports" [value]="sport.key || sport.name">
                                <div class="flex items-center">
                                    <div class="w-6 h-6 rounded mr-2 flex items-center justify-center" 
                                         [style.background-color]="sport.color + '20'">
                                        <img *ngIf="sport.logo" 
                                             [src]="sport.logo" 
                                             [alt]="sport.name"
                                             class="h-4 w-4"
                                             onerror="this.style.display='none'; this.parentElement.innerHTML='<span class=\'text-xs font-bold\' style=\'color: ' + sport.color + '\'>' + sport.name.charAt(0).toUpperCase() + '</span>'">
                                        <span *ngIf="!sport.logo" class="text-xs font-bold" [style.color]="sport.color">
                                            {{sport.name?.charAt(0)?.toUpperCase() || 'S'}}
                                        </span>
                                    </div>
                                    <span>{{sport.name}}</span>
                                </div>
                            </mat-option>
                        </mat-select>
                        <mat-error *ngIf="courtForm.get('sports')?.hasError('required')">
                            Sport is required
                        </mat-error>
                    </mat-form-field>
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                    <!-- Status -->
                    <mat-form-field class="w-full">
                        <mat-label>Status</mat-label>
                        <mat-select formControlName="status" required>
                            <mat-option value="active">Active</mat-option>
                            <mat-option value="inactive">Inactive</mat-option>
                        </mat-select>
                        <mat-error *ngIf="courtForm.get('status')?.hasError('required')">
                            Status is required
                        </mat-error>
                    </mat-form-field>

                    <!-- Court Type -->
                    <mat-form-field class="w-full">
                        <mat-label>Court Type</mat-label>
                        <mat-select formControlName="courtType" required>
                            <mat-option *ngFor="let type of courtTypes" [value]="type">
                                {{type | titlecase}}
                            </mat-option>
                        </mat-select>
                        <mat-error *ngIf="courtForm.get('courtType')?.hasError('required')">
                            Court type is required
                        </mat-error>
                    </mat-form-field>
                </div>
            </div>

            <!-- Court Details -->
            <div class="mb-4">
                <h3 class="text-lg font-semibold mb-3 flex items-center">
                    <mat-icon class="mr-2">sports_tennis</mat-icon>
                    Court Details
                </h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <!-- Surface Type -->
                    <mat-form-field class="w-full">
                        <mat-label>Surface Type</mat-label>
                        <mat-select formControlName="surfaceType" required>
                            <mat-option *ngFor="let surface of surfaceTypes" [value]="surface">
                                {{surface | titlecase}}
                            </mat-option>
                        </mat-select>
                        <mat-error *ngIf="courtForm.get('surfaceType')?.hasError('required')">
                            Surface type is required
                        </mat-error>
                    </mat-form-field>

                    <!-- Air Conditioned -->
                    <div class="flex items-center gap-3 mt-6">
                        <mat-checkbox formControlName="airConditioned" color="primary">
                            Air Conditioned
                        </mat-checkbox>
                        <mat-icon 
                            matTooltip="Indicates if the court has air conditioning"
                            matTooltipPosition="above"
                            class="text-gray-400 text-base cursor-help">
                            info
                        </mat-icon>
                    </div>
                </div>
            </div>

            <!-- Capacity -->
            <div class="mb-4">
                <h3 class="text-lg font-semibold mb-3 flex items-center">
                    <mat-icon class="mr-2">groups</mat-icon>
                    Capacity
                </h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <!-- Min Capacity -->
                    <mat-form-field class="w-full">
                        <mat-label>Minimum Capacity</mat-label>
                        <input matInput type="number" formControlName="minCapacity" required min="1">
                        <mat-error *ngIf="courtForm.get('minCapacity')?.hasError('required')">
                            Minimum capacity is required
                        </mat-error>
                        <mat-error *ngIf="courtForm.get('minCapacity')?.hasError('min')">
                            Minimum capacity must be at least 1
                        </mat-error>
                    </mat-form-field>

                    <!-- Max Capacity -->
                    <mat-form-field class="w-full">
                        <mat-label>Maximum Capacity</mat-label>
                        <input matInput type="number" formControlName="maxCapacity" required min="1">
                        <mat-error *ngIf="courtForm.get('maxCapacity')?.hasError('required')">
                            Maximum capacity is required
                        </mat-error>
                        <mat-error *ngIf="courtForm.get('maxCapacity')?.hasError('min')">
                            Maximum capacity must be at least 1
                        </mat-error>
                    </mat-form-field>
                </div>
            </div>

            <!-- Pricing -->
            <div class="mb-4">
                <h3 class="text-lg font-semibold mb-3 flex items-center">
                    <mat-icon class="mr-2">attach_money</mat-icon>
                    Pricing
                </h3>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <!-- Price Per Hour -->
                    <mat-form-field class="w-full">
                        <mat-label>Price Per Hour (AED)</mat-label>
                        <input matInput type="number" formControlName="pricePerHour" required min="0" step="0.01">
                        <mat-icon matPrefix class="mr-2">money</mat-icon>
                        <mat-error *ngIf="courtForm.get('pricePerHour')?.hasError('required')">
                            Price is required
                        </mat-error>
                        <mat-error *ngIf="courtForm.get('pricePerHour')?.hasError('min')">
                            Price cannot be negative
                        </mat-error>
                    </mat-form-field>
                </div>
            </div>

            <!-- Pictures -->
            <div class="mb-4">
                <h3 class="text-lg font-semibold mb-3 flex items-center">
                    <mat-icon class="mr-2">photo_library</mat-icon>
                    Court Pictures
                </h3>
                <div class="bg-gray-50 rounded-lg p-4">
                    <p class="text-xs text-gray-500 mb-4">
                        Upload high quality images with min resolution 1920x1920
                        and size under 5MB in supported format (PNG, JPG)
                    </p>

                    <!-- Pictures Grid -->
                    <div class="pictures-grid">
                        <!-- Image thumbnails -->
                        <div class="image-thumbnail-wrapper" *ngFor="let picture of pictures; let i = index; trackBy: trackByPicture">
                            <div class="image-thumbnail" (click)="openImageViewer(i)">
                                <ng-container *ngIf="typeof picture === 'string'">
                                    <img [src]="picture" 
                                         alt="Court image {{ i + 1 }}"
                                         class="thumbnail-image">
                                </ng-container>
                                <ng-container *ngIf="isFile(picture)">
                                    <img [src]="getFilePreview(picture)" 
                                         alt="Court image {{ i + 1 }}"
                                         class="thumbnail-image">
                                </ng-container>
                                <div class="thumbnail-overlay">
                                    <button 
                                        type="button" 
                                        (click)="removePicture(i); $event.stopPropagation()"
                                        class="delete-button"
                                        mat-icon-button
                                        [attr.aria-label]="'Delete image ' + (i + 1)">
                                        <mat-icon>delete</mat-icon>
                                    </button>
                                </div>
                            </div>
                        </div>
                        <!-- Add image button -->
                        <div class="upload-box" (click)="openFilePicker()">
                            <mat-icon class="text-blue-600" style="font-size: 48px; width: 48px; height: 48px;">add</mat-icon>
                        </div>
                        <input type="file" 
                               #fileInput
                               (change)="onPictureSelect($event)"
                               multiple
                               accept="image/*"
                               style="display: none;">
                    </div>

                    <!-- Empty State -->
                    <div *ngIf="pictures.length === 0" class="text-center py-8">
                        <mat-icon class="text-gray-300 mb-2" style="width: 48px; height: 48px; font-size: 48px;">
                            photo_library
                        </mat-icon>
                        <p class="text-gray-500">No pictures uploaded yet</p>
                    </div>
                </div>
            </div>

            <!-- Actions -->
            <div class="flex justify-end gap-3 mt-4 pt-4 border-t">
                <button mat-button type="button" (click)="onCancel()" [disabled]="isLoading">
                    Cancel
                </button>
                <button mat-flat-button type="submit" color="primary" [disabled]="isLoading || courtForm.invalid">
                    <span *ngIf="!isLoading">Save Changes</span>
                    <span *ngIf="isLoading">Saving...</span>
                </button>
            </div>
        </form>
    </div>
</div>
